"use client"

/**
 * Form section — Payload-style tinted block (round 4).
 *
 * Design intent:
 * - Previous rounds used hairline borders (L-delta 0.09 light mode) which
 *   were perceptually invisible. Rounds before that used full card chrome
 *   which nested visually with antd widgets. Sweet spot per Payload / Sanity:
 *   a SUBTLE background tint block, no border, no shadow.
 * - Section label is uppercase + muted so it groups the content without
 *   competing with the page title or field labels. Four distinct treatments:
 *     Page title:      24/600 normal, --admin-foreground
 *     Section label:   12/600 UPPERCASE tracking 0.06em, --admin-muted-fg
 *     Field label:     13/500 (antd default), --admin-foreground
 *     Input text:      14/400 (antd default)
 * - `compact` prop: sidebar sections need tighter internal rhythm than
 *   main-column sections. Main column = 16 20 padding / 14 size space.
 *   Compact = 12 14 padding / 10 size space.
 * - `forceOpen` still supported so ErrorSummary-triggered scroll can
 *   auto-expand a collapsed section containing a validation failure.
 */

import { CaretDownOutlined } from "@ant-design/icons"
import { ReactNode, useState } from "react"

interface FormSectionProps {
  title: ReactNode
  children: ReactNode
  defaultOpen?: boolean
  collapsible?: boolean
  /** Right-side slot on the section header (e.g. "Đã tùy chỉnh" tag). */
  badge?: ReactNode
  /** Force the section open externally (e.g. when an error lives inside). */
  forceOpen?: boolean
  description?: ReactNode
  /** Sidebar-density padding (12 14) instead of main-column 16 20. */
  compact?: boolean
  /**
   * @deprecated kept for backward compat with round-3 callers. The new
   * tinted-block sections no longer use dividers between them, so this
   * prop has no effect.
   */
  noDivider?: boolean
  className?: string
}

export function FormSection({
  title,
  children,
  defaultOpen = true,
  collapsible = true,
  badge,
  forceOpen,
  description,
  compact = false,
}: FormSectionProps) {
  const [open, setOpen] = useState(defaultOpen)
  const isOpen = forceOpen ?? open

  const toggle = () => {
    if (!collapsible) return
    setOpen(prev => !prev)
  }

  // Tinted block geometry — padding differs by density.
  const padding = compact ? "12px 14px" : "16px 20px"
  const headerMb = isOpen ? (compact ? 10 : 14) : 0
  const titleSize = compact ? 11 : 12

  return (
    <section
      style={{
        // White card on slightly off-white admin page bg. The combination
        // of background contrast (surface vs background) + 1px border +
        // subtle shadow makes sections unmissable as grouped units.
        background: "var(--admin-surface)",
        border: "1px solid var(--admin-border)",
        borderRadius: 8,
        padding,
        marginBottom: 12,
        boxShadow: "0 1px 2px oklch(0 0 0 / 0.04)",
      }}
    >
      <header
        onClick={toggle}
        role={collapsible ? "button" : undefined}
        aria-expanded={collapsible ? isOpen : undefined}
        tabIndex={collapsible ? 0 : -1}
        onKeyDown={e => {
          if (!collapsible) return
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault()
            toggle()
          }
        }}
        className="flex items-center gap-2 select-none"
        style={{
          cursor: collapsible ? "pointer" : "default",
          marginBottom: headerMb,
        }}
      >
        <h3
          className="m-0"
          style={{
            fontSize: titleSize,
            fontWeight: 600,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: "var(--admin-muted-fg)",
            lineHeight: 1.4,
          }}
        >
          {title}
        </h3>
        {description && (
          <span
            className="truncate"
            style={{
              fontSize: 11,
              color: "var(--admin-muted-fg)",
              fontWeight: 400,
              textTransform: "none",
              letterSpacing: 0,
              opacity: 0.85,
            }}
          >
            · {description}
          </span>
        )}
        <div style={{ flex: 1 }} />
        {badge}
        {collapsible && (
          <CaretDownOutlined
            aria-hidden
            style={{
              fontSize: 10,
              color: "var(--admin-muted-fg)",
              transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)",
              transition: "transform 150ms ease-out",
            }}
          />
        )}
      </header>
      {isOpen && <div>{children}</div>}
    </section>
  )
}

export default FormSection
